import { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Message, Icon, TextArea } from 'semantic-ui-react';
import moment from 'moment';
import styled from '@emotion/styled';
import Swal from 'sweetalert2';
import groupService from '../../services/api/groupService';
import { errorInput, scrollUp } from '../../components/helpers';
import { guestData, renderAnswers } from '../../components/helpers/renderFormAnswers';
import {
  kindOfGroups,
  kindOfSeminaries,
  newLeadersQuestions,
} from '../../containers/groups/newLeaders/newLeadersQuestions';
import { screenSizes } from 'src/assets/styles/Literals';

const ViewComp = styled.div`
  width: 80%;
  max-width: ${screenSizes.desktop};
  margin: 0 auto;
  padding: 2rem;

  .Registration__form {
    background: white;
    text-align: start;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 2rem;
  }

  h1 {
    color: #2c3e50;
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    color: #666;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  .metadata {
    margin: 1rem 0 1.5rem;

    p {
      margin: 0.35rem 0;
      color: #444;
    }
  }

  .prospect {
    margin-top: 1.5rem;

    h2.title {
      padding: 1rem 0;
      color: #2185d0;
      text-align: center;
    }

    .question {
      margin: 1.25rem 0;

      h2,
      h3 {
        color: #2c3e50;
      }

      .answer,
      p {
        margin: 0.5rem 0;
        color: #555;
        line-height: 1.5;
      }

      .answer {
        font-style: italic;
      }
    }
  }

  .guest-block {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;

    &:last-of-type {
      border-bottom: none;
    }

    h3 {
      color: #2185d0;
      margin-bottom: 0.75rem;
    }
  }

  .leader-survey {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 1.75rem;
    margin-top: 2.5rem;

    h2 {
      color: #2c3e50;
      font-size: 1.5rem;
      margin: 0 0 1rem;
      text-align: center;
    }

    .survey-intro,
    .survey-closing {
      p {
        color: #555;
        line-height: 1.7;
        margin: 0.75rem 0;
        text-align: justify;
      }
    }

    .survey-closing {
      margin-top: 1.5rem;
      padding-top: 1.25rem;
      border-top: 1px solid #e2e8f0;
    }

    .personal-info {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 1.25rem 1.5rem;
      margin: 1.5rem 0;

      h3 {
        color: #2c3e50;
        margin: 0 0 1rem;
        font-size: 1.1rem;
      }

      label {
        display: block;
        margin: 0.75rem 0 0.35rem;
        color: #444;
        font-weight: 500;

        &.error {
          color: #dc3545;
        }
      }

      .ui.input {
        width: 100%;
        max-width: 400px;
      }
    }

    .survey-question {
      margin: 1.5rem 0;

      .question-text {
        color: #2c3e50;
        font-weight: 600;
        line-height: 1.6;
        margin-bottom: 0.75rem;
      }

      .question-note {
        color: #666;
        font-size: 0.95rem;
        margin-bottom: 0.75rem;
      }

      &.error-block .question-text {
        color: #dc3545;
      }

      .ui.checkbox {
        display: block;
        margin: 0.5rem 0;

        label {
          font-size: 1rem;
          color: #495057;
        }
      }

      textarea {
        width: 100%;
        min-height: 120px;
        border-radius: 8px;
        border: 1px solid #ced4da;
        padding: 0.875rem;
        font-family: inherit;
        line-height: 1.5;
        resize: vertical;
      }
    }
  }

  @media screen and (max-width: 750px) {
    width: 100%;
    padding: 0.5rem;

    .Registration__form,
    .leader-survey {
      padding: 1rem;
    }
  }
`;

const LEADER_FIELDS = ['name', 'lastName', 'identification'];
const RECOMMEND_OPTIONS = ['Sí', 'No'];

const getFormTypeLabel = (answers) =>
  answers?.info?.info_couple_seminary ??
  [...kindOfGroups, ...kindOfSeminaries]?.find((kind) => kind.value === answers?.type)?.text ??
  answers?.type;

const getLeaderObservationsFromAnswers = (answers) => ({
  name: answers?.leader_observations?.name ?? '',
  lastName: answers?.leader_observations?.lastName ?? '',
  identification: answers?.leader_observations?.identification ?? '',
  observations: answers?.leader_observations?.observations ?? '',
});

const NewLeaderLeaderView = ({ match }) => {
  const { leaderToken } = match.params;
  const [answers, setAnswers] = useState(null);
  const [leaderObservations, setLeaderObservations] = useState({
    name: '',
    lastName: '',
    identification: '',
    observations: '',
  });
  const [recommendContinue, setRecommendContinue] = useState('');
  const [errorInputs, setErrorInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error, setError] = useState('');

  const fetchAnswers = () => {
    setLoading(true);
    setError('');
    groupService
      .getNewLeaderByToken({ leader_token: leaderToken })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setAnswers(response.data.newLeader);
          setLeaderObservations(getLeaderObservationsFromAnswers(response.data.newLeader));
        } else {
          setError(response.data.message || 'No se encontró el formulario.');
        }
      })
      .catch((err) => {
        setLoading(false);
        setError(err?.response?.data?.message || 'No se pudo cargar el formulario.');
      });
  };

  useEffect(() => {
    scrollUp();
    fetchAnswers();
  }, [leaderToken]);

  const validateForm = () => {
    let errors = {};
    LEADER_FIELDS.forEach((field) => {
      errors = errorInput({
        errorInputs: errors,
        input: { name: field, value: leaderObservations[field] },
      });
    });
    if (!recommendContinue) {
      errors.recommendContinue = true;
    }
    setErrorInputs(errors);
    return !Object.values(errors).includes(true);
  };

  const handleLeaderChange = (event) => {
    const { name, value } = event.target;
    setLeaderObservations((prev) => ({ ...prev, [name]: value }));
    setErrorInputs((prev) => ({ ...prev, [name]: false }));
  };

  const submitResponse = () => {
    if (!validateForm()) {
      Swal.fire('Completa los campos obligatorios de la encuesta.');
      return;
    }

    setSending(true);
    groupService
      .updateNewLeaderByToken({
        leader_token: leaderToken,
        leader_observations: leaderObservations,
        survey: recommendContinue === 'Sí',
      })
      .then((response) => {
        setSending(false);
        if (response.data.success) {
          setSubmitted(true);
          setSubmitResult({
            survey: response.data.survey,
            leader_observations: response.data.leader_observations,
            message: response.data.message,
          });
        } else {
          Swal.fire(response?.data?.message);
        }
      })
      .catch((err) => {
        setSending(false);
        Swal.fire(err?.response?.data?.message || 'No se pudo guardar la información.');
      });
  };

  if (loading) {
    return (
      <ViewComp>
        <div className="Registration__form">
          <p>Cargando formulario...</p>
        </div>
      </ViewComp>
    );
  }

  if (submitted) {
    return (
      <ViewComp>
        <div className="Registration__form">
          <Message success>
            <Icon name="check circle" />
            <Message.Content>
              <Message.Header>Encuesta enviada</Message.Header>
              <p>{submitResult?.message}</p>
              <p>
                ¿Recomienda continuar con el proceso?:{' '}
                <strong>{submitResult?.survey ? 'Sí' : 'No'}</strong>
              </p>
              {!!submitResult?.leader_observations?.name && (
                <p>
                  Líder:{' '}
                  <strong>
                    {submitResult.leader_observations.name}{' '}
                    {submitResult.leader_observations.lastName}
                  </strong>{' '}
                  — {submitResult.leader_observations.identification}
                </p>
              )}
              {!!submitResult?.leader_observations?.observations && (
                <p>
                  Observaciones: <em>{submitResult.leader_observations.observations}</em>
                </p>
              )}
              <div className="survey-closing" style={{ marginTop: '1rem' }}>
                <p>Gracias por tomarte el tiempo para responder esta encuesta.</p>
                <p>
                  Valoramos profundamente tu disposición para servir y acompañar a otras personas en
                  su caminar con Dios. ¡Nos estaremos contactando contigo!
                </p>
              </div>
            </Message.Content>
          </Message>
        </div>
      </ViewComp>
    );
  }

  if (error || !answers) {
    return (
      <ViewComp>
        <div className="Registration__form">
          <Message negative>
            <Icon name="warning circle" />
            <Message.Content>{error || 'Formulario no encontrado.'}</Message.Content>
          </Message>
        </div>
      </ViewComp>
    );
  }

  return (
    <ViewComp>
      <div className="Registration__form">
        <h1>Encuesta para el líder</h1>

        <div className="survey-intro">
          <p>Gracias por acompañar y caminar con las personas que desean servir.</p>
          <p>
            Te invitamos a responder con sinceridad, amor y responsabilidad, pensando tanto en el
            bienestar del postulante como en el cuidado de las personas que serán acompañadas.
          </p>
          <p className="subtitle">
            Revisa la información del postulante antes de responder la encuesta. Este enlace solo
            puede responderse una vez.
          </p>
        </div>

        <div className="metadata">
          <p>
            Tipo de formulario: <strong>{getFormTypeLabel(answers)}</strong>
          </p>
          <p>
            Fecha de llenado:{' '}
            <strong>{moment.tz(answers.createdAt, 'America/Bogota').format('DD-MM-YYYY')}</strong>
          </p>
        </div>

        {!!answers.maleNewLeader?.id && (
          <div className="guest-block">
            <h3>Datos de él</h3>
            {guestData(answers.maleNewLeader)}
            {answers.maleNewLeader?.GuestLeader?.leaderFullName && (
              <p>
                Líder: <strong>{answers.maleNewLeader.GuestLeader.leaderFullName}</strong> —{' '}
                {answers.maleNewLeader.GuestLeader.leaderPhone}
              </p>
            )}
          </div>
        )}

        {!!answers.femaleNewLeader?.id && (
          <div className="guest-block">
            <h3>Datos de ella</h3>
            {guestData(answers.femaleNewLeader)}
            {answers.femaleNewLeader?.GuestLeader?.leaderFullName && (
              <p>
                Líder: <strong>{answers.femaleNewLeader.GuestLeader.leaderFullName}</strong> —{' '}
                {answers.femaleNewLeader.GuestLeader.leaderPhone}
              </p>
            )}
          </div>
        )}

        <div className="prospect">
          <h2 className="title">Formulario del Nuevo Aspirante</h2>
          {renderAnswers(newLeadersQuestions[answers?.type], answers)}
        </div>

        <div className="leader-survey">
          <h2>Tus comentarios como líder</h2>
          <div className="personal-info">
            <h3>Tus datos como líder</h3>
            <label className={errorInputs.name ? 'error' : ''} htmlFor="name">
              Nombre {errorInputs.name && '(Campo requerido)'}
            </label>
            <Input
              id="name"
              name="name"
              value={leaderObservations.name}
              disabled={sending}
              error={Boolean(errorInputs.name)}
              onChange={handleLeaderChange}
              placeholder="Nombre"
            />
            <label className={errorInputs.lastName ? 'error' : ''} htmlFor="lastName">
              Apellido {errorInputs.lastName && '(Campo requerido)'}
            </label>
            <Input
              id="lastName"
              name="lastName"
              value={leaderObservations.lastName}
              disabled={sending}
              error={Boolean(errorInputs.lastName)}
              onChange={handleLeaderChange}
              placeholder="Apellido"
            />
            <label className={errorInputs.identification ? 'error' : ''} htmlFor="identification">
              Identificación {errorInputs.identification && '(Campo requerido)'}
            </label>
            <Input
              id="identification"
              name="identification"
              value={leaderObservations.identification}
              disabled={sending}
              error={Boolean(errorInputs.identification)}
              onChange={handleLeaderChange}
              placeholder="Identificación"
            />
          </div>

          <div className={`survey-question ${errorInputs.recommendContinue ? 'error-block' : ''}`}>
            <p className="question-text">
              1. Según tu conocimiento, acompañamiento del postulante y las respuestas anteriormente
              mencionadas, ¿recomendarías continuar con el proceso como facilitador de seminario?
              {errorInputs.recommendContinue && ' (Campo requerido)'}
            </p>
            {RECOMMEND_OPTIONS.map((option) => (
              <Checkbox
                key={option}
                radio
                label={option}
                name="recommendContinue"
                value={option}
                checked={recommendContinue === option}
                disabled={sending}
                onChange={() => {
                  setRecommendContinue(option);
                  setErrorInputs((prev) => ({ ...prev, recommendContinue: false }));
                }}
              />
            ))}
          </div>

          <div className="survey-question">
            <p className="question-text">
              2. ¿Hay alguna observación, recomendación o aspecto importante que consideres
              necesario compartir para este proceso?
            </p>
            <TextArea
              name="observations"
              value={leaderObservations.observations}
              disabled={sending}
              onChange={handleLeaderChange}
              placeholder="Escribe aquí tus observaciones..."
            />
          </div>

          <div className="survey-closing">
            <p>Gracias por tomarte el tiempo para responder esta encuesta.</p>
            <p>
              Valoramos profundamente tu disposición para servir y acompañar a otras personas en su
              caminar con Dios. Nuestro deseo es cuidar tanto tu vida como la de cada participante,
              por eso este proceso busca realizarse con amor, sabiduría y responsabilidad.
            </p>
            <p>
              Oramos para que Dios continúe guiando tu crecimiento espiritual, y te dé claridad en
              cada paso.
            </p>
            <p>¡Nos estaremos contactando contigo!</p>
          </div>

          <Button
            primary
            disabled={sending}
            onClick={submitResponse}
            style={{ marginTop: '1.5rem' }}
          >
            Enviar encuesta
          </Button>
        </div>
      </div>
    </ViewComp>
  );
};

export default NewLeaderLeaderView;
